import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/store/auth";
import axios from "axios";
import { api, apiErrorMessage } from "@/api/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Shield,
  Users,
  LogOut,
  Loader2,
  PowerOff,
  Trash2,
  RefreshCw,
  UserPlus,
  Wifi,
  X,
  LayoutDashboard,
  Download,
  QrCode,
  Copy,
  Check,
  Plus,
  ChevronDown,
  ChevronRight,
  Activity,
  ArrowDownUp,
  RotateCcw,
  Upload,
  Ticket,
  Globe,
  Mail,
  Repeat,
  Power,
  Pencil,
  KeyRound,
} from "lucide-react";
import { QrDialog } from "@/components/QrDialog";
import { CarouselPanel } from "@/components/CarouselPanel";
import { useToast } from "@/hooks/use-toast";
import {
  createUpstream,
  updateUpstream,
  toggleUpstream,
  reapplyUpstream,
  deleteUpstream,
  type UpstreamInput,
} from "@/api/upstreams";
import {
  fetchHideKeys,
  createHideKey,
  updateHideKey,
  deleteHideKey,
  activateHideKey,
  syncUpstreamsNow,
  type HideApiKeyMasked,
} from "@/api/hideKeys";
import {
  fetchGeositeTree,
  setCategoryDefault,
  fetchCategoryDetail,
  fetchCustomCategories,
  createCustomCategory,
  deleteCustomCategory,
  reloadGeosite,
  type GeositeTreeNode,
  type CategoryDetail,
  type CustomCategory,
} from "@/api/geosite";

interface Upstream {
  id: number;
  name: string;
  is_active: boolean;
  interface_up: boolean;
  in_active_set: boolean;
  latency_ms: number | null;
  fail_count: number;
  last_check_at: string | null;
  last_check_result: {
    checked: boolean;
    success: boolean;
    latency_ms: number | null;
    error: string | null;
  };
  download_speed: number | null;
  upload_speed: number | null;
  speedtest_at: string | null;
  total_checks: number;
  total_fails: number;
  success_streak: number;
  fail_streak: number;
  uptime_ratio: number | null;
}

interface UserItem {
  id: number;
  username: string;
  email?: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
}

interface Stats {
  total_users: number;
  active_users: number;
  wg_configs: number;
  upstreams: Upstream[];
}

interface RoutingStats {
  mode: string;
  upstream_connected: boolean;
  blocked_entries_count: number;
  last_update: string | null;
  stats: {
    direct_packets: number;
    vpn_packets: number;
    direct_bytes: number;
    vpn_bytes: number;
  };
  speed_download: number | null;
  speed_upload: number | null;
  speedtest_at: string | null;
}

interface Invite {
  id: number;
  code: string;
  token?: string;
  max_uses: number;
  used_count: number;
  created_by: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

interface UserWgConfig {
  id: number;
  name?: string;
  address?: string;
  created_at: string;
}

type Tab = "upstreams" | "carousel" | "users" | "invites" | "geosite";

function StatCard({
  title,
  value,
  sub,
  icon: Icon,
  active,
  onClick,
}: {
  title: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Card
      className={`border-slate-800 bg-slate-900 cursor-pointer transition-all hover:border-slate-600 ${
        active ? "ring-1 ring-emerald-500/50 border-emerald-500/30" : ""
      }`}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-slate-300">
          {title}
        </CardTitle>
        <Icon
          className={`h-4 w-4 ${active ? "text-emerald-400" : "text-slate-400"}`}
        />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function TreeNodeRender({
  node,
  map,
  depth,
  expanded,
  toggle,
  openDetail,
  handleSetDefault,
}: {
  node: GeositeTreeNode;
  map: Map<string, GeositeTreeNode>;
  depth: number;
  expanded: Set<string>;
  toggle: (name: string) => void;
  openDetail: (name: string) => void;
  handleSetDefault: (name: string, routing: string) => void;
}) {
  const isExpanded = expanded.has(node.name);
  const hasChildren = node.children.length > 0;
  return (
    <div>
      <div
        className="flex items-center gap-2 py-1.5 px-3 hover:bg-slate-800/30 cursor-pointer border-b border-slate-800/20"
        style={{ paddingLeft: `${12 + depth * 20}px` }}
      >
        {hasChildren ? (
          <span onClick={(e) => { e.stopPropagation(); toggle(node.name); }}>
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            )}
          </span>
        ) : (
          <span className="w-3.5" />
        )}
        <span
          onClick={() => openDetail(node.name)}
          className="flex-1 text-slate-300 font-medium text-sm truncate"
          title={node.name}
        >
          {node.name}
        </span>
        <span className="text-xs text-slate-500 tabular-nums">{node.domain_count.toLocaleString()}</span>
        <span onClick={(e) => e.stopPropagation()}>
          <select
            value={node.default_routing}
            onChange={(e) => handleSetDefault(node.name, e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value="vpn">Secure</option>
            <option value="direct">Direct</option>
          </select>
        </span>
      </div>
      {isExpanded &&
        node.children.map((childName) => {
          const child = map.get(childName);
          if (!child) return null;
          return (
            <TreeNodeRender
              key={childName}
              node={child}
              map={map}
              depth={depth + 1}
              expanded={expanded}
              toggle={toggle}
              openDetail={openDetail}
              handleSetDefault={handleSetDefault}
            />
          );
        })}
    </div>
  );
}

export function AdminDashboard() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("upstreams");
  const [qrOpen, setQrOpen] = useState<{ id: number; title: string } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    email: "",
    is_admin: false,
  });
  const [creating, setCreating] = useState(false);



  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [userConfigs, setUserConfigs] = useState<Record<number, UserWgConfig[]>>({});
  const [userConfigsLoading, setUserConfigsLoading] = useState<Record<number, boolean>>({});
  const [routingStats, setRoutingStats] = useState<RoutingStats | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [newInviteMaxUses, setNewInviteMaxUses] = useState(1);
  const [newInviteExpires, setNewInviteExpires] = useState("");
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [sendEmailInviteId, setSendEmailInviteId] = useState<number | null>(null);
  const [sendEmailValue, setSendEmailValue] = useState("");
  const [geositeTree, setGeositeTree] = useState<GeositeTreeNode[]>([]);
  const [geositeLoading, setGeositeLoading] = useState(false);
  const [customCategories, setCustomCategories] = useState<CustomCategory[]>([]);
  const [customLoading, setCustomLoading] = useState(false);
  const [newCustomName, setNewCustomName] = useState("");
  const [newCustomDisplay, setNewCustomDisplay] = useState("");
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [reloadingDlc, setReloadingDlc] = useState(false);
  const [geositeSearch, setGeositeSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCategory, setDetailCategory] = useState<CategoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const emptyUpstreamForm = {
    name: "",
    endpoint: "",
    public_key: "",
    preshared_key: "",
    client_private_key: "",
    client_address: "",
    awg_jc: "",
    awg_jmin: "",
    awg_jmax: "",
    awg_s1: "",
    awg_s2: "",
    awg_h1: "",
    awg_h2: "",
    awg_h3: "",
    awg_h4: "",
    country: "",
    city: "",
  };
  const [upstreamModal, setUpstreamModal] = useState<{
    mode: "create" | "edit";
    upstream?: Upstream;
  } | null>(null);
  const [upstreamForm, setUpstreamForm] = useState(emptyUpstreamForm);
  const [savingUpstream, setSavingUpstream] = useState(false);
  const [hideKeys, setHideKeys] = useState<HideApiKeyMasked[]>([]);
  const [hideKeysLoading, setHideKeysLoading] = useState(true);
  const [hideKeyModal, setHideKeyModal] = useState<{
    mode: "create" | "edit";
    key?: HideApiKeyMasked;
  } | null>(null);
  const [hideKeyForm, setHideKeyForm] = useState({ code: "", label: "", priority: "" });
  const [savingHideKey, setSavingHideKey] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/users"),
      ]);
      setStats(s.data);
      setUsers(u.data.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const routingRequestRef = useRef<AbortController | null>(null);
  const routingInFlightRef = useRef(false);

  const loadRoutingStats = useCallback(async () => {
    if (routingInFlightRef.current) return;
    if (routingRequestRef.current) {
      routingRequestRef.current.abort();
    }
    routingRequestRef.current = new AbortController();
    routingInFlightRef.current = true;
    try {
      const res = await api.get("/api/routing/status", {
        signal: routingRequestRef.current.signal,
      });
      setRoutingStats(res.data);
    } catch (e) {
      if (!axios.isCancel(e)) console.error(e);
    } finally {
      routingInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    Promise.all([
      api.get("/api/admin/stats"),
      api.get("/api/admin/users"),
    ])
      .then(([s, u]) => {
        if (mounted) {
          setStats(s.data);
          setUsers(u.data.users || []);
        }
      })
      .catch((err) => {
        if (mounted) console.error(err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    loadRoutingStats();

    api.get("/api/admin/invites")
      .then((res) => {
        if (mounted) setInvites(res.data.invites || []);
      })
      .catch((err) => {
        if (mounted) console.error(err);
      })
      .finally(() => {
        if (mounted) setInvitesLoading(false);
      });

    fetchHideKeys()
      .then((res) => {
        if (mounted) setHideKeys(res.data.keys || []);
      })
      .catch((err) => {
        if (mounted) console.error(err);
      })
      .finally(() => {
        if (mounted) setHideKeysLoading(false);
      });

    Promise.all([fetchGeositeTree(), fetchCustomCategories()])
      .then(([catRes, customRes]) => {
        if (mounted) {
          setGeositeTree(catRes.data.categories || []);
          setCustomCategories(customRes.data.categories || []);
        }
      })
      .catch((err) => {
        if (mounted) console.error(err);
      })
      .finally(() => {
        if (mounted) {
          setGeositeLoading(false);
          setCustomLoading(false);
        }
      });

    const interval = setInterval(loadRoutingStats, 5000);

    return () => {
      mounted = false;
      clearInterval(interval);
      routingRequestRef.current?.abort();
    };
  }, [loadRoutingStats]);

  const loadInvites = async () => {
    setInvitesLoading(true);
    try {
      const res = await api.get("/api/admin/invites");
      setInvites(res.data.invites || []);
    } catch (e) {
      console.error(e);
    } finally {
      setInvitesLoading(false);
    }
  };

  const loadGeosite = async () => {
    setGeositeLoading(true);
    setCustomLoading(true);
    try {
      const [catRes, customRes] = await Promise.all([
        fetchGeositeTree(),
        fetchCustomCategories(),
      ]);
      setGeositeTree(catRes.data.categories || []);
      setCustomCategories(customRes.data.categories || []);
    } catch (e) {
      console.error(e);
    } finally {
      setGeositeLoading(false);
      setCustomLoading(false);
    }
  };

  const handleSetDefault = async (name: string, routing: string) => {
    try {
      await setCategoryDefault(name, routing);
      await loadGeosite();
    } catch (e) {
      console.error(e);
    }
  };

  const openDetail = async (name: string) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await fetchCategoryDetail(name);
      setDetailCategory(res.data);
    } catch (e) {
      console.error(e);
      setDetailCategory(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateCustom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomName.trim()) return;
    setCreatingCustom(true);
    try {
      await createCustomCategory({
        name: newCustomName.trim(),
        display_name: newCustomDisplay.trim() || undefined,
      });
      setNewCustomName("");
      setNewCustomDisplay("");
      await loadGeosite();
    } catch (e: unknown) {
      alert(apiErrorMessage(e, "Ошибка создания"));
    } finally {
      setCreatingCustom(false);
    }
  };

  const handleDeleteCustom = async (id: number) => {
    if (!confirm("Удалить категорию?")) return;
    try {
      await deleteCustomCategory(id);
      await loadGeosite();
    } catch {
      alert("Ошибка удаления");
    }
  };

  const handleReloadDlc = async () => {
    setReloadingDlc(true);
    try {
      await reloadGeosite();
      alert("DLC обновлен");
      await loadGeosite();
    } catch {
      alert("Ошибка обновления");
    } finally {
      setReloadingDlc(false);
    }
  };

  const createInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingInvite(true);
    try {
      const formData = new URLSearchParams();
      formData.append("max_uses", String(newInviteMaxUses));
      if (newInviteExpires) {
        formData.append("expires_at", newInviteExpires);
      }
      const res = await api.post("/api/admin/invites", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data.success) {
        setNewInviteMaxUses(1);
        setNewInviteExpires("");
        await loadInvites();
      } else {
        alert(res.data.error || "Ошибка создания");
      }
    } catch (e: unknown) {
      alert(apiErrorMessage(e, "Ошибка создания"));
    } finally {
      setCreatingInvite(false);
    }
  };

  const deactivateInvite = async (id: number) => {
    if (!confirm("Деактивировать код?")) return;
    try {
      await api.delete(`/api/admin/invites/${id}`);
      await loadInvites();
    } catch {
      alert("Ошибка деактивации");
    }
  };

  const sendInviteEmail = async (id: number) => {
    if (!sendEmailValue.trim()) return;
    try {
      const formData = new URLSearchParams();
      formData.append("email", sendEmailValue.trim());
      await api.post(`/api/admin/invites/${id}/send-email`, formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      setSendEmailInviteId(null);
      setSendEmailValue("");
      alert("Приглашение отправлено на email");
    } catch (e: unknown) {
      alert(apiErrorMessage(e, "Ошибка отправки"));
    }
  };

  const resetRoutingStats = async () => {
    try {
      await api.post("/api/routing/reset-stats");
      await loadRoutingStats();
    } catch {
      alert("Ошибка сброса статистики");
    }
  };

  const toggleUserConfigs = async (userId: number) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }
    setExpandedUser(userId);
    if (!userConfigs[userId]) {
      setUserConfigsLoading((prev) => ({ ...prev, [userId]: true }));
      try {
        const res = await api.get(`/api/admin/user-configs?user_id=${userId}`);
        setUserConfigs((prev) => ({ ...prev, [userId]: res.data.configs || [] }));
      } catch (e) {
        console.error(e);
      } finally {
        setUserConfigsLoading((prev) => ({ ...prev, [userId]: false }));
      }
    }
  };

  const deleteMyConfig = async (id: number) => {
    if (!confirm("Удалить конфиг?")) return;
    try {
      await api.post(`/api/admin/configs/wg/${id}/delete`);
      // Refresh expanded user configs if open
      if (expandedUser) {
        const res = await api.get(`/api/admin/user-configs?user_id=${expandedUser}`);
        setUserConfigs((prev) => ({ ...prev, [expandedUser]: res.data.configs || [] }));
      }
    } catch {
      alert("Ошибка удаления");
    }
  };

  const deleteUser = async (id: number) => {
    if (!confirm("Удалить пользователя?")) return;
    setActionId(id);
    try {
      await api.post(`/api/admin/users/${id}/delete`);
      await loadAll();
    } catch {
      alert("Ошибка удаления");
    } finally {
      setActionId(null);
    }
  };

  const generateForUser = async (id: number) => {
    setActionId(id);
    try {
      await api.post(`/api/admin/users/${id}/wg-config`);
      alert(`WG конфиг создан`);
      if (expandedUser === id) {
        toggleUserConfigs(id);
      }
    } catch {
      alert("Ошибка");
    } finally {
      setActionId(null);
    }
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const formData = new URLSearchParams();
      formData.append("username", newUser.username);
      formData.append("password", newUser.password);
      formData.append("display_name", newUser.username);
      if (newUser.email.trim()) {
        formData.append("email", newUser.email.trim());
      }
      formData.append("is_admin", newUser.is_admin ? "1" : "0");
      const res = await api.post("/api/admin/users", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data.success) {
        setShowModal(false);
        setNewUser({ username: "", password: "", email: "", is_admin: false });
        await loadAll();
      } else {
        alert(res.data.error || "Ошибка создания");
      }
    } catch (e: unknown) {
      alert(apiErrorMessage(e, "Ошибка создания"));
    } finally {
      setCreating(false);
    }
  };

  const refreshStats = async () => {
    try {
      const s = await api.get("/api/admin/stats");
      setStats(s.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadHideKeys = async () => {
    setHideKeysLoading(true);
    try {
      const res = await fetchHideKeys();
      setHideKeys(res.data.keys || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHideKeysLoading(false);
    }
  };

  const openUpstreamModal = (mode: "create" | "edit", upstream?: Upstream) => {
    setUpstreamModal({ mode, upstream });
    setUpstreamForm(
      mode === "edit" && upstream
        ? { ...emptyUpstreamForm, name: upstream.name }
        : { ...emptyUpstreamForm }
    );
  };

  const submitUpstreamModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!upstreamModal) return;
    setSavingUpstream(true);
    try {
      const f = upstreamForm;
      const str = (v: string) => (v.trim() === "" ? undefined : v.trim());
      const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
      const input: UpstreamInput = {
        name: str(f.name),
        endpoint: str(f.endpoint),
        public_key: str(f.public_key),
        preshared_key: str(f.preshared_key),
        client_private_key: str(f.client_private_key),
        client_address: str(f.client_address),
        awg_jc: num(f.awg_jc),
        awg_jmin: num(f.awg_jmin),
        awg_jmax: num(f.awg_jmax),
        awg_s1: num(f.awg_s1),
        awg_s2: num(f.awg_s2),
        awg_h1: num(f.awg_h1),
        awg_h2: num(f.awg_h2),
        awg_h3: num(f.awg_h3),
        awg_h4: num(f.awg_h4),
        country: str(f.country),
        city: str(f.city),
      };
      if (upstreamModal.mode === "create") {
        const res = await createUpstream(input);
        if (res.data.success) {
          toast("Upstream создан", "success");
          setUpstreamModal(null);
          await refreshStats();
        } else {
          toast(res.data.error || "Ошибка создания");
        }
      } else if (upstreamModal.upstream) {
        const res = await updateUpstream(upstreamModal.upstream.id, input);
        if (res.data.success) {
          toast("Upstream обновлён", "success");
          setUpstreamModal(null);
          await refreshStats();
        } else {
          toast(res.data.error || "Ошибка обновления");
        }
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка сохранения"));
    } finally {
      setSavingUpstream(false);
    }
  };

  const handleToggleUpstream = async (u: Upstream) => {
    if (u.is_active) {
      if (!confirm(`Выключить upstream «${u.name}»? Интерфейс будет корректно остановлен (graceful teardown).`)) return;
    }
    setActionId(u.id);
    try {
      const res = await toggleUpstream(u.id);
      if (res.data.success) {
        toast(res.data.is_active ? `Upstream «${u.name}» включён` : `Upstream «${u.name}» выключен`, "success");
        await refreshStats();
      } else {
        toast(res.data.error || "Ошибка переключения");
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка переключения"));
    } finally {
      setActionId(null);
    }
  };

  const handleReapplyUpstream = async (u: Upstream) => {
    if (!confirm(`Пересоздать интерфейс для «${u.name}» из БД? Возможен кратковременный разрыв соединения.`)) return;
    setActionId(u.id);
    try {
      const res = await reapplyUpstream(u.id);
      if (res.data.success) {
        toast(`Интерфейс «${u.name}» пересоздан`, "success");
        await refreshStats();
      } else {
        toast(res.data.error || "Ошибка reapply");
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка reapply"));
    } finally {
      setActionId(null);
    }
  };

  const handleDeleteUpstream = async (u: Upstream) => {
    if (!confirm(`Удалить upstream «${u.name}»? Интерфейс будет остановлен (teardown), клиенты будут перемещены на fallback.`)) return;
    setActionId(u.id);
    try {
      const res = await deleteUpstream(u.id);
      if (res.data.success) {
        toast(`Upstream «${u.name}» удалён`, "success");
        await refreshStats();
      } else {
        toast(res.data.error || "Ошибка удаления");
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка удаления"));
    } finally {
      setActionId(null);
    }
  };

  const openHideKeyModal = (mode: "create" | "edit", key?: HideApiKeyMasked) => {
    setHideKeyModal({ mode, key });
    setHideKeyForm(
      mode === "edit" && key
        ? { code: "", label: key.label, priority: String(key.priority) }
        : { code: "", label: "", priority: "" }
    );
  };

  const submitHideKeyModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hideKeyModal) return;
    setSavingHideKey(true);
    try {
      const label = hideKeyForm.label.trim();
      const priority = hideKeyForm.priority.trim() === "" ? undefined : Number(hideKeyForm.priority);
      if (hideKeyModal.mode === "create") {
        const res = await createHideKey({
          code: hideKeyForm.code.trim(),
          label: label || undefined,
          priority,
        });
        if (res.data.success) {
          toast("Ключ добавлен", "success");
          setHideKeyModal(null);
          await loadHideKeys();
        } else {
          toast(res.data.error || "Ошибка создания");
        }
      } else if (hideKeyModal.key) {
        const code = hideKeyForm.code.trim();
        const res = await updateHideKey(hideKeyModal.key.id, {
          label: label || undefined,
          priority,
          code: code || undefined,
        });
        if (res.data.success) {
          toast("Ключ обновлён", "success");
          setHideKeyModal(null);
          await loadHideKeys();
        } else {
          toast(res.data.error || "Ошибка обновления");
        }
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка сохранения"));
    } finally {
      setSavingHideKey(false);
    }
  };

  const handleDeleteHideKey = async (key: HideApiKeyMasked) => {
    if (!confirm(`Удалить ключ ${key.code_masked}${key.label ? ` («${key.label}»)` : ""}?`)) return;
    try {
      const res = await deleteHideKey(key.id);
      if (res.data.success) {
        toast("Ключ удалён", "success");
        await loadHideKeys();
      } else {
        toast(res.data.error || "Ошибка удаления");
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка удаления"));
    }
  };

  const handleActivateHideKey = async (key: HideApiKeyMasked) => {
    try {
      const res = await activateHideKey(key.id);
      if (res.data.success) {
        toast(`Ключ ${key.code_masked} активирован`, "success");
        await loadHideKeys();
      } else {
        toast(res.data.error || "Ошибка активации");
      }
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка активации"));
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await syncUpstreamsNow();
      if (res.data.success) {
        toast(`Sync: ${res.data.mode}`, "success");
      } else {
        toast(`Ошибка sync: ${res.data.mode}`);
      }
      await loadHideKeys();
    } catch (err: unknown) {
      toast(apiErrorMessage(err, "Ошибка sync"));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-emerald-400" />
            <h1 className="text-xl font-bold">Gateway Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Мои конфиги
              </Button>
            </Link>
            <span className="text-sm text-slate-400">{user?.username}</span>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto space-y-6">
        {/* eBPF Routing Stats */}
        <Card className="border-slate-800 bg-slate-900">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-400" />
              Статистика маршрутизации (eBPF)
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={resetRoutingStats} className="text-slate-400 hover:text-white">
              <RotateCcw className="mr-2 h-3 w-3" />
              Сбросить
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-6">
              <div className="space-y-1">
                <div className="text-xs text-slate-500">Direct пакеты</div>
                <div className="text-xl font-bold">{routingStats?.stats?.direct_packets ?? 0}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-slate-500">Защищенные пакеты</div>
                <div className="text-xl font-bold text-emerald-400">{routingStats?.stats?.vpn_packets ?? 0}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-slate-500">Direct байты</div>
                <div className="text-xl font-bold">{routingStats?.stats?.direct_bytes ? (routingStats.stats.direct_bytes / 1024 / 1024).toFixed(2) + ' MB' : '0 MB'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-slate-500">Защищенный трафик</div>
                <div className="text-xl font-bold text-emerald-400">{routingStats?.stats?.vpn_bytes ? (routingStats.stats.vpn_bytes / 1024 / 1024).toFixed(2) + ' MB' : '0 MB'}</div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Download className="h-3 w-3" />
                  Скорость ↓
                </div>
                <div className="text-xl font-bold text-blue-400">
                  {routingStats?.speed_download != null
                    ? (routingStats.speed_download / 1000).toFixed(1) + ' Mbps'
                    : '—'}
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Upload className="h-3 w-3" />
                  Скорость ↑
                </div>
                <div className="text-xl font-bold text-amber-400">
                  {routingStats?.speed_upload != null
                    ? (routingStats.speed_upload / 1000).toFixed(1) + ' Mbps'
                    : '—'}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
              <ArrowDownUp className="h-3 w-3" />
              Режим: <span className="text-slate-300">{routingStats?.mode ?? '—'}</span>
              | Блоклист: <span className="text-slate-300">{routingStats?.blocked_entries_count ?? 0}</span> записей
              | Upstream: <span className={routingStats?.upstream_connected ? "text-emerald-400" : "text-red-400"}>{routingStats?.upstream_connected ? 'ONLINE' : 'OFFLINE'}</span>
              {routingStats?.speedtest_at && (
                <>
                  | Speedtest: <span className="text-slate-300">{new Date(routingStats.speedtest_at).toLocaleString('ru-RU')}</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats cards as tabs */}
        <div className="grid gap-4 md:grid-cols-5">
          <StatCard
            title="Пользователи"
            value={loading ? "..." : stats?.total_users ?? 0}
            sub={`активных: ${loading ? "..." : stats?.active_users ?? 0}`}
            icon={Users}
            active={activeTab === "users"}
            onClick={() => setActiveTab("users")}
          />

          <StatCard
            title="Upstreams"
            value={
              loading
                ? "..."
                : stats?.upstreams.filter((u) => u.is_active).length ?? 0
            }
            sub={`всего: ${loading ? "..." : stats?.upstreams.length ?? 0}`}
            icon={RefreshCw}
            active={activeTab === "upstreams"}
            onClick={() => setActiveTab("upstreams")}
          />
          <StatCard
            title="Карусель"
            value={
              loading
                ? "..."
                : stats?.upstreams.filter((u) => u.in_active_set).length ?? 0
            }
            sub="в active set"
            icon={Repeat}
            active={activeTab === "carousel"}
            onClick={() => setActiveTab("carousel")}
          />
          <StatCard
            title="Invite-ссылки"
            value={invitesLoading ? "..." : invites.filter((i) => i.is_active).length}
            sub={`всего: ${invitesLoading ? "..." : invites.length}`}
            icon={Ticket}
            active={activeTab === "invites"}
            onClick={() => setActiveTab("invites")}
          />
          <StatCard
            title="Geosite"
            value={geositeLoading ? "..." : geositeTree.length}
            sub={`категорий`}
            icon={Globe}
            active={activeTab === "geosite"}
            onClick={() => setActiveTab("geosite")}
          />
        </div>

        {/* Upstreams tab */}
        {activeTab === "upstreams" && (
          <>
          <Card className="border-slate-800 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Upstream серверы</CardTitle>
              <Button size="sm" variant="secondary" onClick={() => openUpstreamModal("create")}>
                <Plus className="mr-2 h-4 w-4" />
                Добавить upstream
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400">
                      <th className="text-left py-2 px-3">ID</th>
                      <th className="text-left py-2 px-3">Название</th>
                      <th className="text-left py-2 px-3">Статус</th>
                      <th className="text-left py-2 px-3">Active set</th>
                      <th className="text-left py-2 px-3">Latency</th>
                      <th className="text-left py-2 px-3">Fails</th>
                      <th className="text-left py-2 px-3">Стабильность</th>
                      <th className="text-left py-2 px-3">Проверка</th>
                      <th className="text-left py-2 px-3">Результат</th>
                      <th className="text-left py-2 px-3">Интерфейс</th>
                      <th className="text-right py-2 px-3">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11} className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : (
                      stats?.upstreams.map((u) => (
                        <tr
                          key={u.id}
                          className={`border-b border-slate-800/50 hover:bg-slate-800/30 ${
                            u.in_active_set ? "bg-emerald-950/20" : ""
                          }`}
                        >
                          <td className="py-2 px-3">{u.id}</td>
                          <td className="py-2 px-3">{u.name}</td>
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center gap-1 text-xs ${
                                u.is_active
                                  ? "text-emerald-400"
                                  : "text-red-400"
                              }`}
                            >
                              {u.is_active ? "ON" : "OFF"}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center gap-1 text-xs ${
                                u.in_active_set
                                  ? "text-emerald-400"
                                  : "text-slate-500"
                              }`}
                            >
                              {u.in_active_set ? "IN" : "OUT"}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-slate-500">{u.latency_ms != null ? u.latency_ms + ' ms' : '—'}</td>
                          <td className="py-2 px-3">{u.fail_count}</td>
                          <td className="py-2 px-3">
                            <div className="flex flex-col">
                              <span
                                className={
                                  u.uptime_ratio == null
                                    ? "text-slate-500"
                                    : u.uptime_ratio >= 0.95
                                      ? "text-emerald-400"
                                      : u.uptime_ratio >= 0.8
                                        ? "text-amber-400"
                                        : "text-red-400"
                                }
                              >
                                {u.uptime_ratio != null
                                  ? `${(u.uptime_ratio * 100).toFixed(1)}%`
                                  : "—"}
                              </span>
                              <span className="text-xs">
                                {u.success_streak > 0 ? (
                                  <span className="text-emerald-400">{u.success_streak}✓</span>
                                ) : u.fail_streak > 0 ? (
                                  <span className="text-red-400">{u.fail_streak}✗</span>
                                ) : (
                                  <span className="text-slate-600">—</span>
                                )}
                              </span>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-slate-500">
                            {u.last_check_at
                              ? new Date(u.last_check_at).toLocaleTimeString(
                                  "ru-RU"
                                )
                              : "—"}
                          </td>
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center gap-1 text-xs ${
                                !u.last_check_result?.checked
                                  ? "text-slate-500"
                                  : u.last_check_result?.success
                                    ? "text-emerald-400"
                                    : "text-red-400"
                              }`}
                              title={u.last_check_result?.error ?? undefined}
                            >
                              {!u.last_check_result?.checked
                                ? "—"
                                : u.last_check_result?.success
                                  ? "OK"
                                  : "FAIL"}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-slate-500">
                            {u.interface_up
                              ? `awg-upstream-${u.id}`
                              : '—'}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title={u.is_active ? "Выключить (graceful teardown)" : "Включить"}
                                onClick={() => handleToggleUpstream(u)}
                                disabled={actionId === u.id}
                              >
                                {u.is_active ? (
                                  <PowerOff className="h-4 w-4 text-amber-400" />
                                ) : (
                                  <Power className="h-4 w-4 text-emerald-400" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Пересоздать интерфейс из БД"
                                onClick={() => handleReapplyUpstream(u)}
                                disabled={actionId === u.id}
                              >
                                <RotateCcw className="h-4 w-4 text-blue-400" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Редактировать"
                                onClick={() => openUpstreamModal("edit", u)}
                                disabled={actionId === u.id}
                              >
                                <Pencil className="h-4 w-4 text-slate-300" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Удалить"
                                onClick={() => handleDeleteUpstream(u)}
                                disabled={actionId === u.id}
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Hide keys section */}
          <Card className="border-slate-800 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-emerald-400" />
                Hide ключи
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleSyncNow} disabled={syncing} className="border-slate-700">
                  {syncing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Sync сейчас
                </Button>
                <Button size="sm" variant="secondary" onClick={() => openHideKeyModal("create")}>
                  <Plus className="mr-2 h-4 w-4" />
                  Добавить ключ
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400">
                      <th className="text-left py-2 px-3">Label</th>
                      <th className="text-left py-2 px-3">Ключ</th>
                      <th className="text-left py-2 px-3">Статус</th>
                      <th className="text-left py-2 px-3">Priority</th>
                      <th className="text-left py-2 px-3">Последний sync</th>
                      <th className="text-left py-2 px-3">Результат</th>
                      <th className="text-right py-2 px-3">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hideKeysLoading ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : hideKeys.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-500">
                          Нет ключей
                        </td>
                      </tr>
                    ) : (
                      hideKeys.map((key) => (
                        <tr key={key.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-2 px-3">
                            {key.label || <span className="text-slate-600">—</span>}
                          </td>
                          <td className="py-2 px-3 font-mono text-xs text-slate-300">
                            {key.code_masked}
                          </td>
                          <td className="py-2 px-3">
                            {key.is_active ? (
                              <span className="inline-flex items-center rounded bg-emerald-500/10 px-1.5 py-0.5 text-xs text-emerald-400">
                                ACTIVE
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3">{key.priority}</td>
                          <td className="py-2 px-3 text-slate-500">
                            {key.last_sync_at
                              ? new Date(key.last_sync_at).toLocaleString("ru-RU")
                              : "—"}
                          </td>
                          <td className="py-2 px-3">
                            {key.last_sync_ok == null ? (
                              <span className="text-xs text-slate-500">—</span>
                            ) : key.last_sync_ok ? (
                              <span className="text-xs text-emerald-400">OK</span>
                            ) : (
                              <span
                                className="text-xs text-red-400 cursor-help"
                                title={key.last_error ?? undefined}
                              >
                                FAIL
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex gap-1 justify-end">
                              {!key.is_active && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  title="Активировать (снимается с остальных)"
                                  onClick={() => handleActivateHideKey(key)}
                                >
                                  <Power className="h-4 w-4 text-emerald-400" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Редактировать"
                                onClick={() => openHideKeyModal("edit", key)}
                              >
                                <Pencil className="h-4 w-4 text-slate-300" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Удалить"
                                onClick={() => handleDeleteHideKey(key)}
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </>
        )}

        {/* Carousel tab */}
        {activeTab === "carousel" && (
          <CarouselPanel upstreams={stats?.upstreams ?? []} />
        )}

        {/* Users tab with expandable configs */}
        {activeTab === "users" && (
          <Card className="border-slate-800 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Пользователи</CardTitle>
              <Button size="sm" variant="secondary" onClick={() => setShowModal(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Добавить
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400">
                      <th className="text-left py-2 px-3 w-8"></th>
                      <th className="text-left py-2 px-3">ID</th>
                      <th className="text-left py-2 px-3">Логин</th>
                      <th className="text-left py-2 px-3">Email</th>
                      <th className="text-left py-2 px-3">Роль</th>
                      <th className="text-left py-2 px-3">Статус</th>
                      <th className="text-left py-2 px-3">Создан</th>
                      <th className="text-right py-2 px-3">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : (
                      users.map((u) => (
                        <React.Fragment key={u.id}>
                          <tr
                            className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer"
                            onClick={() => toggleUserConfigs(u.id)}
                          >
                            <td className="py-2 px-3">
                              {expandedUser === u.id ? (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              )}
                            </td>
                            <td className="py-2 px-3">{u.id}</td>
                            <td className="py-2 px-3 font-medium">{u.username}</td>
                            <td className="py-2 px-3 text-slate-400">{u.email || "—"}</td>
                            <td className="py-2 px-3">
                              <span className={`text-xs ${u.is_admin ? "text-amber-400" : "text-slate-400"}`}>
                                {u.is_admin ? "admin" : "user"}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <span className={`text-xs ${u.is_active ? "text-emerald-400" : "text-red-400"}`}>
                                {u.is_active ? "active" : "inactive"}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-slate-500">
                              {new Date(u.created_at).toLocaleDateString("ru-RU")}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); generateForUser(u.id); }}
                                  disabled={actionId === u.id}
                                  title="WG конфиг"
                                >
                                  <Wifi className="h-4 w-4 text-emerald-400" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); deleteUser(u.id); }}
                                  disabled={actionId === u.id}
                                  title="Удалить"
                                >
                                  <Trash2 className="h-4 w-4 text-red-400" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {expandedUser === u.id && (
                            <tr>
                              <td colSpan={8} className="py-0">
                                <div className="bg-slate-800/20 px-4 py-3">
                                  {userConfigsLoading[u.id] ? (
                                    <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                                  ) : (userConfigs[u.id] || []).length === 0 ? (
                                    <p className="text-sm text-slate-500">Нет конфигураций</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {(userConfigs[u.id] || []).map((cfg: UserWgConfig) => (
                                        <div key={cfg.id} className="flex items-center justify-between bg-slate-900/50 rounded px-3 py-2">
                                          <div className="flex items-center gap-2">
                                            <Wifi className="h-3 w-3 text-emerald-400" />
                                            <span className="text-sm">
                                              {cfg.address}
                                            </span>
                                            <span className="text-xs text-slate-500">
                                              {new Date(cfg.created_at).toLocaleDateString("ru-RU")}
                                            </span>
                                          </div>
                                          <div className="flex gap-1">
                                            <a href={`/api/config/wg/${cfg.id}`} download onClick={(e) => e.stopPropagation()}>
                                              <Button variant="ghost" size="sm"><Download className="h-3 w-3"/></Button>
                                            </a>
                                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setQrOpen({ id: cfg.id, title: `AmneziaWG — ${cfg.name || cfg.address}` }); }}>
                                              <QrCode className="h-3 w-3"/>
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteMyConfig(cfg.id); }}>
                                              <Trash2 className="h-3 w-3 text-red-400" />
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invites tab */}
        {activeTab === "invites" && (
          <Card className="border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle className="text-base">Invite-ссылки</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Create invite form */}
              <form onSubmit={createInvite} className="flex flex-wrap items-end gap-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Макс. использований</label>
                  <Input
                    type="number"
                    min={1}
                    value={newInviteMaxUses}
                    onChange={(e) => setNewInviteMaxUses(Number(e.target.value))}
                    required
                    className="w-40 border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Истекает</label>
                  <Input
                    type="datetime-local"
                    value={newInviteExpires}
                    onChange={(e) => setNewInviteExpires(e.target.value)}
                    className="w-56 border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <Button type="submit" disabled={creatingInvite}>
                  {creatingInvite && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Plus className="mr-2 h-4 w-4" />
                  Создать ссылку
                </Button>
              </form>

              {/* Invites table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400">
                      <th className="text-left py-2 px-3">Ссылка</th>
                      <th className="text-left py-2 px-3">Max использований</th>
                      <th className="text-left py-2 px-3">Использовано</th>
                      <th className="text-left py-2 px-3">Создан</th>
                      <th className="text-left py-2 px-3">Истекает</th>
                      <th className="text-left py-2 px-3">Активен</th>
                      <th className="text-right py-2 px-3">Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitesLoading ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : invites.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-slate-500">
                          Нет invite-ссылок
                        </td>
                      </tr>
                    ) : (
                      invites.map((invite) => {
                        const token = invite.token || invite.code;
                        const inviteUrl = `${window.location.origin}/invite/${token}`;
                        return (
                          <tr
                            key={invite.id}
                            className="border-b border-slate-800/50 hover:bg-slate-800/30"
                          >
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-slate-300 truncate max-w-[280px]">
                                  {inviteUrl}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => {
                                    navigator.clipboard.writeText(inviteUrl);
                                    setCopiedId(invite.id);
                                    setTimeout(() => setCopiedId(null), 2000);
                                  }}
                                >
                                  {copiedId === invite.id ? (
                                    <Check className="h-3 w-3 text-emerald-400" />
                                  ) : (
                                    <Copy className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </td>
                            <td className="py-2 px-3">{invite.max_uses}</td>
                            <td className="py-2 px-3">{invite.used_count}</td>
                            <td className="py-2 px-3 text-slate-500">
                              {new Date(invite.created_at).toLocaleDateString("ru-RU")}
                            </td>
                            <td className="py-2 px-3 text-slate-500">
                              {invite.expires_at
                                ? new Date(invite.expires_at).toLocaleString("ru-RU")
                                : "—"}
                            </td>
                            <td className="py-2 px-3">
                              <span
                                className={`text-xs ${
                                  invite.is_active ? "text-emerald-400" : "text-red-400"
                                }`}
                              >
                                {invite.is_active ? "да" : "нет"}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-1 justify-end">
                                {invite.is_active && sendEmailInviteId === invite.id ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="email"
                                      value={sendEmailValue}
                                      onChange={(e) => setSendEmailValue(e.target.value)}
                                      placeholder="Email"
                                      className="h-8 w-40 text-xs bg-slate-800 border-slate-700"
                                      autoFocus
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => sendInviteEmail(invite.id)}
                                      className="text-blue-400 hover:text-blue-300 px-2"
                                    >
                                      <Mail className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => { setSendEmailInviteId(null); setSendEmailValue(""); }}
                                      className="text-slate-400 hover:text-slate-300 px-2"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : invite.is_active ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSendEmailInviteId(invite.id)}
                                    className="text-blue-400 hover:text-blue-300"
                                  >
                                    <Mail className="h-4 w-4 mr-1" />
                                    Отправить на email
                                  </Button>
                                ) : null}
                                {invite.is_active && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deactivateInvite(invite.id)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <PowerOff className="h-4 w-4 mr-1" />
                                    Деактивировать
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Geosite tab */}
        {activeTab === "geosite" && (
          <Card className="border-slate-800 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Geosite категории</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleReloadDlc} disabled={reloadingDlc} className="border-slate-700">
                  {reloadingDlc && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Обновить dlc
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Search */}
              <div className="flex flex-wrap gap-3">
                <Input
                  placeholder="Поиск категории..."
                  value={geositeSearch}
                  onChange={(e) => setGeositeSearch(e.target.value)}
                  className="w-64 border-slate-700 bg-slate-800 text-white"
                />
                {geositeSearch && (
                  <Button size="sm" variant="ghost" onClick={() => setGeositeSearch("")} className="text-slate-400">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Tree view */}
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-slate-800 rounded-md">
                {geositeLoading ? (
                  <div className="py-8 text-center text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </div>
                ) : (() => {
                  if (geositeTree.length === 0) {
                    return <div className="py-8 text-center text-slate-500">Нет категорий</div>;
                  }
                  const map = new Map<string, GeositeTreeNode>();
                  const allChildren = new Set<string>();
                  for (const n of geositeTree) {
                    map.set(n.name, n);
                    for (const c of n.children) allChildren.add(c);
                  }

                  if (geositeSearch.trim()) {
                    // Flat search: show matching nodes
                    const q = geositeSearch.toLowerCase();
                    const matches = geositeTree.filter((n) => n.name.toLowerCase().includes(q));
                    if (matches.length === 0) {
                      return <div className="py-8 text-center text-slate-500">Нет совпадений</div>;
                    }
                    return (
                      <div>
                        {matches.map((node) => (
                          <TreeNodeRender
                            key={node.name}
                            node={node}
                            map={map}
                            depth={0}
                            expanded={expandedGroups}
                            toggle={(name) => {
                              setExpandedGroups((prev) => {
                                const next = new Set(prev);
                                if (next.has(name)) next.delete(name);
                                else next.add(name);
                                return next;
                              });
                            }}
                            openDetail={openDetail}
                            handleSetDefault={handleSetDefault}
                          />
                        ))}
                      </div>
                    );
                  }

                  // Tree: show only roots
                  const roots = geositeTree
                    .filter((n) => !allChildren.has(n.name))
                    .sort((a, b) => a.name.localeCompare(b.name));

                  return (
                    <div>
                      {roots.map((node) => (
                        <TreeNodeRender
                          key={node.name}
                          node={node}
                          map={map}
                          depth={0}
                          expanded={expandedGroups}
                          toggle={(name) => {
                            setExpandedGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(name)) next.delete(name);
                              else next.add(name);
                              return next;
                            });
                          }}
                          openDetail={openDetail}
                          handleSetDefault={handleSetDefault}
                        />
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Detail modal */}
              {detailOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                  <div className="w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl flex flex-col">
                    <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
                      <h3 className="text-base font-medium text-white">
                        {detailCategory?.name || "Категория"}
                        {detailCategory?.display_name && (
                          <span className="ml-2 text-sm text-slate-400">({detailCategory.display_name})</span>
                        )}
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => setDetailOpen(false)} className="text-slate-400 hover:text-white">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-auto p-4">
                      {detailLoading ? (
                        <div className="py-8 text-center text-slate-500">
                          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                        </div>
                      ) : !detailCategory ? (
                        <div className="py-8 text-center text-slate-500">Ошибка загрузки</div>
                      ) : detailCategory.domains.length === 0 ? (
                        <div className="py-8 text-center text-slate-500">Нет доменов</div>
                      ) : (
                        <div className="space-y-1">
                          {detailCategory.domains.map((d, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-slate-300 py-1 border-b border-slate-800/30">
                              <span className="inline-flex items-center rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-400 font-mono min-w-[80px]">
                                {d.rule_type}
                              </span>
                              <span>{d.domain}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="border-t border-slate-800 px-4 py-3 text-right">
                      <Button size="sm" onClick={() => setDetailOpen(false)}>Закрыть</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom categories */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-300">Кастомные категории</h3>
                
                <form onSubmit={handleCreateCustom} className="flex flex-wrap items-end gap-3">
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Имя (код)</label>
                    <Input
                      value={newCustomName}
                      onChange={(e) => setNewCustomName(e.target.value)}
                      placeholder="mycompany"
                      required
                      className="w-48 border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-slate-400">Отображаемое имя</label>
                    <Input
                      value={newCustomDisplay}
                      onChange={(e) => setNewCustomDisplay(e.target.value)}
                      placeholder="My Company"
                      className="w-48 border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                  <Button type="submit" disabled={creatingCustom} size="sm">
                    {creatingCustom && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Plus className="mr-2 h-4 w-4" />
                    Создать
                  </Button>
                </form>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 text-slate-400">
                        <th className="text-left py-2 px-3">Имя</th>
                        <th className="text-left py-2 px-3">Отображаемое имя</th>
                        <th className="text-center py-2 px-3">Доменов</th>
                        <th className="text-center py-2 px-3">Маршрут</th>
                        <th className="text-right py-2 px-3">Действие</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customLoading ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-slate-500">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                          </td>
                        </tr>
                      ) : customCategories.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-slate-500">
                            Нет кастомных категорий
                          </td>
                        </tr>
                      ) : (
                        customCategories.map((cc) => (
                          <tr key={cc.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                            <td className="py-2 px-3 text-slate-300 font-mono text-xs">{cc.name}</td>
                            <td className="py-2 px-3 text-slate-300">{cc.display_name || "—"}</td>
                            <td className="py-2 px-3 text-center text-slate-300">{cc.domain_count ?? 0}</td>
                            <td className="py-2 px-3 text-center">
                              <select
                                value={cc.default_routing || "direct"}
                                onChange={(e) => handleSetDefault(cc.name, e.target.value)}
                                className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm"
                              >
                                <option value="direct">Direct</option>
                                <option value="vpn">Secure</option>
                              </select>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCustom(cc.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Удалить
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}


      </main>

      {/* Modal for adding user */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md border-slate-700 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Новый пользователь</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={createUser} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Логин</label>
                  <Input
                    value={newUser.username}
                    onChange={(e) =>
                      setNewUser({ ...newUser, username: e.target.value })
                    }
                    placeholder="user1"
                    required
                    className="border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Пароль</label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    placeholder="••••••"
                    required
                    className="border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Email</label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    placeholder="user@example.com"
                    className="border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_admin"
                    checked={newUser.is_admin}
                    onChange={(e) =>
                      setNewUser({ ...newUser, is_admin: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-emerald-500"
                  />
                  <label htmlFor="is_admin" className="text-sm text-slate-300">
                    Администратор
                  </label>
                </div>
                <Button type="submit" className="w-full" disabled={creating}>
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Создать
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal for upstream create/edit */}
      {upstreamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto border-slate-700 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                {upstreamModal.mode === "create" ? "Новый upstream" : `Редактировать: ${upstreamModal.upstream?.name ?? ""}`}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setUpstreamModal(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitUpstreamModal} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Название</label>
                    <Input
                      value={upstreamForm.name}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, name: e.target.value })}
                      placeholder="nl-hide-1"
                      required
                      className="border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Endpoint</label>
                    <Input
                      value={upstreamForm.endpoint}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, endpoint: e.target.value })}
                      placeholder="1.2.3.4:51820"
                      required={upstreamModal.mode === "create"}
                      className="border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Public key сервера</label>
                  <Input
                    value={upstreamForm.public_key}
                    onChange={(e) => setUpstreamForm({ ...upstreamForm, public_key: e.target.value })}
                    placeholder="base64..."
                    required={upstreamModal.mode === "create"}
                    className="border-slate-700 bg-slate-800 text-white font-mono text-xs"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Preshared key</label>
                    <Input
                      type="password"
                      value={upstreamForm.preshared_key}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, preshared_key: e.target.value })}
                      placeholder={upstreamModal.mode === "edit" ? "пусто = не менять" : "необязательно"}
                      className="border-slate-700 bg-slate-800 text-white font-mono text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Client private key</label>
                    <Input
                      type="password"
                      value={upstreamForm.client_private_key}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, client_private_key: e.target.value })}
                      placeholder={upstreamModal.mode === "edit" ? "пусто = не менять" : "необязательно"}
                      className="border-slate-700 bg-slate-800 text-white font-mono text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Client address</label>
                    <Input
                      value={upstreamForm.client_address}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, client_address: e.target.value })}
                      placeholder="10.8.0.2/32"
                      className="border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">AWG параметры</label>
                  <div className="grid grid-cols-3 gap-3 md:grid-cols-9">
                    {(
                      [
                        ["awg_jc", "JC", "3"],
                        ["awg_jmin", "JMin", "50"],
                        ["awg_jmax", "JMax", "100"],
                        ["awg_s1", "S1", "0"],
                        ["awg_s2", "S2", "0"],
                        ["awg_h1", "H1", "1"],
                        ["awg_h2", "H2", "2"],
                        ["awg_h3", "H3", "3"],
                        ["awg_h4", "H4", "4"],
                      ] as const
                    ).map(([field, label, def]) => (
                      <div key={field} className="space-y-1">
                        <label className="text-xs text-slate-400">{label}</label>
                        <Input
                          type="number"
                          value={upstreamForm[field]}
                          onChange={(e) =>
                            setUpstreamForm({ ...upstreamForm, [field]: e.target.value })
                          }
                          placeholder={upstreamModal.mode === "edit" ? "—" : def}
                          className="border-slate-700 bg-slate-800 text-white"
                        />
                      </div>
                    ))}
                  </div>
                  {upstreamModal.mode === "create" && (
                    <p className="text-xs text-slate-500">Дефолты: 3 / 50 / 100 / 0 / 0 / 1 / 2 / 3 / 4</p>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Страна</label>
                    <Input
                      value={upstreamForm.country}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, country: e.target.value })}
                      placeholder="NL"
                      className="border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Город</label>
                    <Input
                      value={upstreamForm.city}
                      onChange={(e) => setUpstreamForm({ ...upstreamForm, city: e.target.value })}
                      placeholder="Amsterdam"
                      className="border-slate-700 bg-slate-800 text-white"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={savingUpstream}>
                  {savingUpstream && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {upstreamModal.mode === "create" ? "Создать" : "Сохранить"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal for hide key create/edit */}
      {hideKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <Card className="w-full max-w-md border-slate-700 bg-slate-900">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                {hideKeyModal.mode === "create" ? "Новый hide-ключ" : "Редактировать ключ"}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setHideKeyModal(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitHideKeyModal} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Код (API key)</label>
                  <Input
                    type="password"
                    value={hideKeyForm.code}
                    onChange={(e) => setHideKeyForm({ ...hideKeyForm, code: e.target.value })}
                    placeholder={
                      hideKeyModal.mode === "edit"
                        ? "пусто = не менять"
                        : "hs_..."
                    }
                    required={hideKeyModal.mode === "create"}
                    className="border-slate-700 bg-slate-800 text-white font-mono text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Label</label>
                  <Input
                    value={hideKeyForm.label}
                    onChange={(e) => setHideKeyForm({ ...hideKeyForm, label: e.target.value })}
                    placeholder="Основной"
                    className="border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Priority</label>
                  <Input
                    type="number"
                    value={hideKeyForm.priority}
                    onChange={(e) => setHideKeyForm({ ...hideKeyForm, priority: e.target.value })}
                    placeholder="0"
                    className="w-32 border-slate-700 bg-slate-800 text-white"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={savingHideKey}>
                  {savingHideKey && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {hideKeyModal.mode === "create" ? "Добавить" : "Сохранить"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {qrOpen && (
        <QrDialog
          configId={qrOpen.id}
          title={qrOpen.title}
          open={!!qrOpen}
          onClose={() => setQrOpen(null)}
        />
      )}
    </div>
  );
}
